import { GetStaticProps, InferGetStaticPropsType, GetStaticPaths } from 'next';
import { isrRevalidateSec } from '@constants';
import { PostListPage } from '@components';
import {
  getHnaviContentCategoriesFromCategories,
  metaDescriptionLounge,
} from '@helpers';
import { basePath, altTagPath } from '@paths';
import { title, shortTitle } from '@constants';
import {
  getAllCategoryNamesAndSlugs2,
  getAltTag,
  getHnaviOptions,
  getLatestPosts,
  getMostPopularPosts,
  getPostCountsOfAllAltTags,
  getPosts,
} from '@api';
import { useCanonical } from '@hooks';
import {
  AltTagIdType,
  RootQueryToPostConnectionWhereArgsTaxQueryField,
  TaxonomyEnum,
} from '@/src/generated/graphql';

const Page: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = ({
  altTag,
  ...rest
}) => {
  return (
    <PostListPage
      {...rest}
      pageCount={Math.ceil(
        (rest.posts?.pageInfo?.offsetPagination?.total || 1) / 10
      )}
      seoData={{
        ...altTag.seo,
        canonical: useCanonical(),
        metaDesc: altTag?.seo?.metaDesc || metaDescriptionLounge,
      }}
      pageTitle={altTag?.name}
      breadcrumbs={[
        {
          text: title,
          shortText: shortTitle,
          url: basePath,
        },
        {
          text: altTag.name,
          url: altTagPath(altTag.slug || altTag.id),
        },
      ]}
      paginationBasePath={altTagPath(altTag.slug || altTag.id)}
    />
  );
};

export default Page;

export const getStaticProps = async ({
  params,
}: Parameters<
  GetStaticProps<any, { slug: string /* カテゴリーslug */; pageNo: string }>
>[0]) => {
  console.info(`getStaticProps /index/${params?.slug}/page/${params?.pageNo}`);

  const categories = await getAllCategoryNamesAndSlugs2();

  const altTag = await getAltTag({
    id: params?.slug || '',
    idType: AltTagIdType.Slug,
  });

  if (!altTag) {
    return { notFound: true, revalidate: isrRevalidateSec } as never;
  }

  const pageNo = Number(params?.pageNo || 1);

  return {
    props: {
      posts: await getPosts({
        where: {
          offsetPagination: {
            size: 10,
            offset: 10 * (pageNo - 1),
          },
          taxQuery: {
            taxArray: [
              {
                taxonomy: TaxonomyEnum.Alttag,
                terms: [params?.slug || ''],
                field: RootQueryToPostConnectionWhereArgsTaxQueryField.Slug,
              },
            ],
          },
        },
      }),
      altTag,
      categories,
      hnaviContentCategories:
        getHnaviContentCategoriesFromCategories(categories),
      latestPosts: await getLatestPosts(),
      popularPosts: await getMostPopularPosts(),
      pageNo,
      hnaviOptions: await getHnaviOptions(),
    },
    revalidate: isrRevalidateSec,
  };
};

type PathParams = {
  slug: string;
  pageNo: string;
};

export const getStaticPaths: GetStaticPaths<PathParams> = async () => {
  const counts = (await getPostCountsOfAllAltTags()) || [];

  return {
    paths: counts
      .reduce<PathParams[]>((acc, { slug, posts }) => {
        if (!slug) {
          return acc;
        }
        return [
          ...acc,
          ...Array(
            Math.ceil(posts?.pageInfo?.offsetPagination?.total || 1 / 10)
          )
            .fill(0)
            .map<PathParams>((_, i) => ({ slug, pageNo: String(1 + i) })),
        ];
      }, [])
      .map((params) => ({ params })),
    fallback: 'blocking',
  };
};
